/**
 * Printing Service - Comprehensive printing operations via PrintNode
 * Handles hierarchical configuration resolution, label printing, document printing, and history.
 */
import { AuthService } from './authService';
import {
  PrintConfiguration,
  ProfileReports,
  CreateProfileReportRequest,
  UpdateProfileReportRequest,
  PrintItemLabelRequest,
  PrintItemLabelWithPORequest,
  PrintItemLabelWithSORequest,
  PrintLocationLabelRequest,
  PrintGenericLabelRequest,
  PrintPackageLabelRequest,
  PrintSSCCLabelRequest,
  PrintPackingSlipRequest,
  PrintBatchPackageLabelsRequest,
  PrintSortingBoxLabelRequest,
  PrintResult,
  PrintLog,
  Report,
  CreatePackageLabelsRequest,
  CreatePackageLabelsResponse,
  ReprintRequest,
  CleanupResult,
  PrintHistorySearchParams,
  PaginatedPrintHistory,
  PrintPickingListRequest,
  PrintCountListRequest,
  PrintPutawayListRequest,
  TablePdfExportRequest,
  ReportType
} from '../types/printing';
import { Printer } from '../types/printNode';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Label type for receiving operations (matches legacy LabelType)
 */
export interface LabelType {
  reportID: number;
  description: string;
  reportType: number;
  /** LabelDesigner-layout (JSON) — brukes til frifelt-deteksjon og forhåndsvisning */
  layoutJson?: string | null;
}

class PrintingService {
  // ==================== SSCC UTILITIES ====================

  /**
   * Generate SSCC code (18 digits)
   * Format: Extension digit (1) + Company prefix (7-10) + Serial reference (6-9) + Check digit (1)
   * This is a client-side helper - actual SSCC should come from backend or GS1 allocation
   */
  generateSSCC(companyPrefix: string, serialRef: number): string {
    // Extension digit (always 0 for standard SSCC)
    const extensionDigit = '0';

    // Pad company prefix to 7 digits (Norwegian GS1 prefix is 703)
    const paddedPrefix = companyPrefix.padStart(7, '0');

    // Calculate remaining digits for serial reference (17 - 1 - prefix length = serial length)
    const serialLength = 17 - 1 - paddedPrefix.length;
    const paddedSerial = serialRef.toString().padStart(serialLength, '0');

    // Combine without check digit
    const ssccWithoutCheck = extensionDigit + paddedPrefix + paddedSerial;

    // Calculate check digit (mod 10)
    const checkDigit = this.calculateMod10CheckDigit(ssccWithoutCheck);

    return ssccWithoutCheck + checkDigit;
  }

  /**
   * Calculate mod 10 check digit for GS1 codes
   */
  private calculateMod10CheckDigit(code: string): string {
    let sum = 0;
    const reversed = code.split('').reverse();

    for (let i = 0; i < reversed.length; i++) {
      const digit = parseInt(reversed[i], 10);
      // Odd positions (from right, 0-indexed) multiply by 3, even by 1
      sum += digit * (i % 2 === 0 ? 3 : 1);
    }

    const remainder = sum % 10;
    return remainder === 0 ? '0' : (10 - remainder).toString();
  }
  // ==================== CONFIGURATION ====================

  /**
   * Resolve print configuration using 4-level hierarchy
   * Hierarchy: PackZone -> ProfileReports -> Profile -> Reports
   */
  async resolvePrintConfiguration(
    companyNo: string,
    reportId: number,
    packZoneId?: string | null,
    profileId?: string | null
  ): Promise<PrintConfiguration> {
    const params = new URLSearchParams({ reportId: reportId.toString() });
    if (packZoneId) params.append('packZoneId', packZoneId);
    if (profileId) params.append('profileId', profileId);

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/printing/configuration/resolve?${params.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Failed to resolve print configuration: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get available PrintNode printers
   */
  async getAvailablePrinters(companyNo: string): Promise<Printer[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/printing/printers`
    );

    if (!response.ok) {
      throw new Error(`Failed to get printers: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get reports, optionally filtered by type. Hvis activeOnly=true filtreres
   * inaktive rapporter bort (default false for bakoverkompatibilitet).
   */
  async getReports(companyNo: string, reportType?: number, activeOnly: boolean = false): Promise<Report[]> {
    let url = `${API_BASE_URL}/${companyNo}/printing/reports`;
    const params = new URLSearchParams();
    if (reportType !== undefined) params.set('reportType', String(reportType));
    if (activeOnly) params.set('activeOnly', 'true');
    const qs = params.toString();
    if (qs) url += `?${qs}`;

    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to get reports: ${response.status}`);
    }

    return await response.json();
  }

  // ==================== LEGACY COMPATIBILITY (for receiving module migration) ====================

  /**
   * Get label types for receiving operations.
   * Inkluderer ItemLabel (ReportType=1), ItemAndPurchaseOrder (ReportType=2) og
   * Diverse (ReportType=60 — generiske etiketter, tilgjengelig i alle moduler)
   * slik at brukeren kan velge mal med innkjøpsordre-info ved varemottak.
   * Filtrerer bort inaktive rapporter (activeOnly=true).
   */
  async getLabelTypesForReceiving(companyNo: string): Promise<LabelType[]> {
    const [itemLabels, itemPOLabels, diverseLabels] = await Promise.all([
      this.getReports(companyNo, 1, true),
      this.getReports(companyNo, 2, true),
      this.getReports(companyNo, ReportType.Diverse, true)
    ]);
    return [...itemLabels, ...itemPOLabels, ...diverseLabels]
      .map(r => ({
        reportID: r.reportID,
        description: r.description,
        reportType: r.reportType,
        layoutJson: r.layoutJson
      }))
      .sort((a, b) => a.reportType - b.reportType || a.description.localeCompare(b.description, 'nb'));
  }

  /**
   * Get SSCC label types (ReportType = 50)
   * Legacy compatibility wrapper. Filtrerer bort inaktive rapporter.
   */
  async getSSCCLabelTypes(companyNo: string): Promise<LabelType[]> {
    // Get reports with reportType 50 (SSCC)
    const reports = await this.getReports(companyNo, 50, true);
    return reports.map(r => ({
      reportID: r.reportID,
      description: r.description,
      reportType: r.reportType
    }));
  }

  /**
   * Get default ReportID for a specific item (Items.ReportID).
   * Calls PrintingController — printing-relatert oppslag.
   */
  async getItemDefaultReport(companyNo: string, itemNo: string): Promise<number | null> {
    const encodedItemNo = encodeURIComponent(itemNo);
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/printing/items/${encodedItemNo}/default-report`
    );

    if (!response.ok) {
      throw new Error(`Failed to get item default report: ${response.status}`);
    }

    const data = await response.json();
    return data.reportID;
  }

  /**
   * Legacy print label interface for receiving module
   * Maps legacy PrintLabelRequest format to new printItemLabel/printItemLabelWithPO
   */
  async printLabel(companyNo: string, request: {
    reportID?: number | null;
    numOfLabels: number;
    itemNo: string;
    itemUnitCode?: string | null;
    orderNo?: number | null;
    lineNo?: number | null;
    locationName?: string | null;
    batchNo?: string | null;
    serialNo?: string | null;
    expiryDate?: string | null;
    quantity?: number | null;
    quantityGross?: number | null;
    reasonCode?: number | null;
    pOrderNo?: number | null;
    pOrderLineNo?: number | null;
    supplierName?: string | null;
    printerIdOverride?: number | null;
    freeTextValues?: Record<string, string>;
  }): Promise<{ success: boolean; message: string; labelsProcessed?: number }> {
    // Map to the appropriate new request format
    // If pOrderNo is present, use printItemLabelWithPO, otherwise use printItemLabel
    if (request.pOrderNo) {
      const newRequest: PrintItemLabelWithPORequest = {
        reportId: request.reportID,
        numOfCopies: request.numOfLabels,
        itemNo: request.itemNo,
        batch: request.batchNo,
        expiryDate: request.expiryDate,
        quantity: request.quantity,
        locationName: request.locationName,
        pOrderNo: request.pOrderNo,
        pOrderLineNo: request.pOrderLineNo,
        supplierName: request.supplierName,
        printerIdOverride: request.printerIdOverride,
        freeTextValues: request.freeTextValues
      };

      const result = await this.printItemLabelWithPO(companyNo, newRequest);
      return {
        success: result.success,
        message: result.message,
        labelsProcessed: result.labelsPrinted
      };
    } else {
      const newRequest: PrintItemLabelRequest = {
        reportId: request.reportID,
        numOfCopies: request.numOfLabels,
        itemNo: request.itemNo,
        batch: request.batchNo,
        expiryDate: request.expiryDate,
        quantity: request.quantity,
        locationName: request.locationName,
        printerIdOverride: request.printerIdOverride,
        freeTextValues: request.freeTextValues
      };

      const result = await this.printItemLabel(companyNo, newRequest);
      return {
        success: result.success,
        message: result.message,
        labelsProcessed: result.labelsPrinted
      };
    }
  }

  /**
   * Legacy SSCC print interface for receiving module
   * Maps legacy PrintSSCCLabelRequest format to new printSSCCLabel
   */
  async printSSCCLabelLegacy(companyNo: string, request: {
    reportId?: number | null;
    numOfCopies?: number | null;
    sscc: string;
    gtin?: string | null;
    batchNo?: string | null;
    expiryDate?: string | null;
    bestBeforeDate?: string | null;
    quantity?: number | null;
    serialNo?: string | null;
    itemNo?: string | null;
    itemDescription?: string | null;
    companyName?: string | null;
    companyAddress?: string | null;
  }): Promise<{ success: boolean; message: string; labelsProcessed?: number }> {
    const newRequest: PrintSSCCLabelRequest = {
      reportId: request.reportId,
      numOfCopies: request.numOfCopies ?? 1,
      sscc: request.sscc,
      gtin: request.gtin,
      batchNo: request.batchNo,
      expiryDate: request.expiryDate,
      quantity: request.quantity
    };

    const result = await this.printSSCCLabel(companyNo, newRequest);
    return {
      success: result.success,
      message: result.message,
      labelsProcessed: result.labelsPrinted
    };
  }

  // ==================== LABEL PRINTING ====================

  /**
   * Print item label (ReportType 1)
   */
  async printItemLabel(companyNo: string, request: PrintItemLabelRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/labels/item`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print item label'));
    }

    return await response.json();
  }

  /**
   * Print item label with purchase order info (ReportType 2)
   */
  async printItemLabelWithPO(companyNo: string, request: PrintItemLabelWithPORequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/labels/item-with-po`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print item label with PO'));
    }

    return await response.json();
  }

  /**
   * Print item label with sales order info (ReportType 3)
   */
  async printItemLabelWithSO(companyNo: string, request: PrintItemLabelWithSORequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/labels/item-with-so`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print item label with SO'));
    }

    return await response.json();
  }

  /**
   * Print location/bin label (ReportType 4/5)
   */
  async printLocationLabel(companyNo: string, request: PrintLocationLabelRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/labels/location`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print location label'));
    }

    return await response.json();
  }

  /**
   * Print a generic label (Norlux P5, ReportType 60 Diverse) — collection-box, fritekst-etiketter m.m.
   */
  async printGenericLabel(companyNo: string, request: PrintGenericLabelRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/labels/generic`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print generic label'));
    }

    return await response.json();
  }

  /**
   * Print package/colli label
   */
  async printPackageLabel(companyNo: string, request: PrintPackageLabelRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/labels/package`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print package label'));
    }

    return await response.json();
  }

  /**
   * Print SSCC/pallet label with GS1-128 barcodes
   */
  async printSSCCLabel(companyNo: string, request: PrintSSCCLabelRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/labels/sscc`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print SSCC label'));
    }

    return await response.json();
  }

  /**
   * Print batch package labels for batch picking
   * Simple labels showing VisiblePackageNo (large) and PackageNo as barcode
   */
  async printBatchPackageLabels(companyNo: string, request: PrintBatchPackageLabelsRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/labels/batch-package`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print batch package labels'));
    }

    return await response.json();
  }

  /**
   * Print a sorting box label (sorteringskasse)
   * One label per zone (StcBoxTypeID) with StcBoxNo as Code128 barcode
   */
  async printSortingBoxLabel(companyNo: string, request: PrintSortingBoxLabelRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/labels/sorting-box`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print sorting box label'));
    }

    return await response.json();
  }

  // ==================== DOCUMENT PRINTING ====================

  /**
   * Print packing slip
   */
  async printPackingSlip(companyNo: string, request: PrintPackingSlipRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/documents/packing-slip`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print packing slip'));
    }

    return await response.json();
  }

  // ==================== PRINT HISTORY ====================

  /**
   * Get recent print history
   */
  async getPrintHistory(companyNo: string, limit: number = 50): Promise<PrintLog[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/printing/history?limit=${limit}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get print history: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get print history for a specific reference
   */
  async getPrintHistoryByReference(
    companyNo: string,
    referenceType: string,
    referenceId: string
  ): Promise<PrintLog[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/printing/history/${encodeURIComponent(referenceType)}/${encodeURIComponent(referenceId)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get print history: ${response.status}`);
    }

    return await response.json();
  }

  // ==================== PROFILE REPORTS ====================

  /**
   * Get all profile report configurations
   */
  async getAllProfileReports(companyNo: string): Promise<ProfileReports[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/profilereports`
    );

    if (!response.ok) {
      throw new Error(`Failed to get profile reports: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get profile report configurations for a specific profile
   */
  async getProfileReportsByProfile(companyNo: string, profileId: string): Promise<ProfileReports[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/profilereports/profile/${encodeURIComponent(profileId)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get profile reports: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get specific profile report configuration
   */
  async getProfileReportByProfileAndReport(
    companyNo: string,
    profileId: string,
    reportId: number
  ): Promise<ProfileReports | null> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/profilereports/profile/${encodeURIComponent(profileId)}/report/${reportId}`
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to get profile report: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get profile report configuration by ID
   */
  async getProfileReportById(companyNo: string, id: number): Promise<ProfileReports | null> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/profilereports/${id}`
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to get profile report: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Create a new profile report configuration
   */
  async createProfileReport(companyNo: string, request: CreateProfileReportRequest): Promise<ProfileReports> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/profilereports`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to create profile report'));
    }

    return await response.json();
  }

  /**
   * Update a profile report configuration
   */
  async updateProfileReport(
    companyNo: string,
    id: number,
    request: UpdateProfileReportRequest
  ): Promise<ProfileReports> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/${companyNo}/profilereports/${id}`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to update profile report'));
    }

    return await response.json();
  }

  /**
   * Delete a profile report configuration
   */
  async deleteProfileReport(companyNo: string, id: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${API_BASE_URL}/${companyNo}/profilereports/${id}`
    );

    if (!response.ok && response.status !== 204) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to delete profile report'));
    }
  }

  // ==================== PACKAGE LABEL CREATION ====================

  /**
   * Create package labels and optionally print them.
   * Creates X separate rows in PListOrderPackages (standalone) or Packages (batch) table.
   * Each row represents one physical package with a unique PackageID that can be printed as a barcode.
   * This follows the legacy pattern where 5 labels = 5 database rows, not 1 row with quantity 5.
   */
  async createAndPrintPackageLabels(request: CreatePackageLabelsRequest): Promise<CreatePackageLabelsResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/packing/packages/create-and-print`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to create package labels'));
    }

    return await response.json();
  }

  // ==================== REPRINT ====================

  /**
   * Reprint a previously printed document
   */
  async reprint(companyNo: string, request: ReprintRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/reprint`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to reprint'));
    }

    return await response.json();
  }

  /**
   * Search print history with filters and pagination
   */
  async searchPrintHistory(companyNo: string, params: PrintHistorySearchParams): Promise<PaginatedPrintHistory> {
    const queryParams = new URLSearchParams();
    if (params.referenceType) queryParams.append('referenceType', params.referenceType);
    if (params.referenceId) queryParams.append('referenceId', params.referenceId);
    if (params.operatorId) queryParams.append('operatorId', params.operatorId);
    if (params.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params.toDate) queryParams.append('toDate', params.toDate);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset != null) queryParams.append('offset', params.offset.toString());

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/printing/history/search?${queryParams.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Failed to search print history: ${response.status}`);
    }

    const items: PrintLog[] = await response.json();
    const totalCount = parseInt(response.headers.get('X-Total-Count') || '0', 10);

    return { items, totalCount };
  }

  /**
   * Get a specific print log entry
   */
  async getPrintLogById(companyNo: string, logId: number): Promise<PrintLog | null> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/printing/history/log/${logId}`
    );

    if (response.status === 404) return null;

    if (!response.ok) {
      throw new Error(`Failed to get print log: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Download the archived PDF for a print log entry
   */
  async downloadPrintLogPdf(companyNo: string, logId: number): Promise<Blob> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/printing/history/log/${logId}/pdf`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to download PDF'));
    }

    return await response.blob();
  }

  /**
   * Cleanup old print logs (admin function)
   */
  async cleanupPrintLogs(companyNo: string, retentionDays: number = 30): Promise<CleanupResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/cleanup?retentionDays=${retentionDays}`,
      {}
    );

    if (!response.ok) {
      throw new Error(`Failed to cleanup print logs: ${response.status}`);
    }

    return await response.json();
  }

  // ==================== PROCESS DOCUMENTS ====================

  async printPickingList(companyNo: string, request: PrintPickingListRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/documents/picking-list`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print picking list'));
    }

    return await response.json();
  }

  async printCountList(companyNo: string, request: PrintCountListRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/documents/count-list`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print count list'));
    }

    return await response.json();
  }

  async printPutawayList(companyNo: string, request: PrintPutawayListRequest): Promise<PrintResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/documents/putaway-list`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(extractApiError(error, response, 'Failed to print putaway list'));
    }

    return await response.json();
  }

  // ==================== TABLE EXPORT ====================

  async exportTableAsPdf(companyNo: string, request: TablePdfExportRequest): Promise<Blob> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/printing/documents/table-export`,
      request
    );

    if (!response.ok) {
      throw new Error(`Failed to generate table PDF: ${response.status}`);
    }

    return await response.blob();
  }
}

export const printingService = new PrintingService();
